import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchExpenses } from '@/services/expenses';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Expense } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Pagination } from '@/components/common/Pagination';
import { SearchInput } from '@/components/common/SearchInput';
import { ExpenseForm } from '@/features/expenses/ExpenseForm';
import {
  DollarSign,
  Plus,
  ChevronDown,
  CalendarDays,
  Download,
  Loader2,
  FileText,
} from 'lucide-react';
import { PAGE_SIZE } from '@/lib/constants';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export const ExpensesPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [page, setPage] = useState(0);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.expenses.list({ search, category, paymentMethod, page }),
    queryFn: () => fetchExpenses({ search, category, paymentMethod, page }),
    placeholderData: (prev) => prev,
  });

  const expenses = data?.data ?? [];
  const totalCount = data?.count ?? 0;
  const pageTotal = data?.totalAmount ?? 0;

  const handleSearch = (val: string) => {
    setSearch(val);
    setPage(0);
  };

  const handleExportCsv = () => {
    if (expenses.length === 0) return;
    const headers = ['Date', 'Category', 'Description', 'Vendor', 'Amount', 'Payment Method', 'Notes'];
    const rows = expenses.map((e) => [
      e.expense_date,
      `"${e.category}"`,
      `"${e.description.replace(/"/g, '""')}"`,
      `"${e.vendor || ''}"`,
      e.amount,
      e.payment_method || '',
      `"${(e.notes || '').replace(/"/g, '""')}"`,
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `alkuwait_expenses_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-5 text-xs">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-brand-black flex items-center space-x-2">
            <DollarSign className="w-5 h-5 text-brand-red" />
            <span>Shop Operating Expenses</span>
          </h1>
          <p className="text-brand-muted mt-0.5">
            {isLoading ? 'Loading…' : `${totalCount.toLocaleString('en-PK')} logged expense entries`}
          </p>
        </div>

        <div className="flex items-center space-x-2 shrink-0">
          <button
            onClick={handleExportCsv}
            disabled={expenses.length === 0}
            className="inline-flex items-center space-x-1.5 px-3 py-2 bg-white text-brand-black font-semibold rounded border border-brand-border hover:bg-brand-bg disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
          <button
            onClick={() => {
              setSelectedExpense(null);
              setFormModalOpen(true);
            }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white font-bold rounded-md hover:bg-brand-green-hover shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Log Expense</span>
          </button>
        </div>
      </div>

      {/* KPI Banner */}
      <div className="p-3 bg-red-50/60 border border-red-200 rounded-lg flex items-center justify-between text-xs">
        <div>
          <span className="font-semibold text-red-900 uppercase">Filtered Expenses Subtotal</span>
          <p className="text-base font-extrabold text-red-700">{formatCurrency(pageTotal)}</p>
        </div>
        <span className="text-[10px] text-red-800">
          Owner & Manager Financial Records
        </span>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <SearchInput value={search} onChange={handleSearch} placeholder="Search description, vendor or notes..." />
        </div>

        <div className="relative">
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(0);
            }}
            className="h-9 pl-3 pr-8 bg-white border border-brand-border rounded-md text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green appearance-none"
          >
            <option value="">All Categories</option>
            <option value="Rent & Shop Lease">Rent & Shop Lease</option>
            <option value="Electricity & Utilities">Electricity & Utilities</option>
            <option value="Internet & Telecom">Internet & Telecom</option>
            <option value="Staff Salary & Food">Staff Salary & Food</option>
            <option value="Packaging & Printing">Packaging & Printing</option>
            <option value="Freight & Courier Charges">Freight & Courier Charges</option>
            <option value="Repair Tools & Consumables">Repair Tools & Consumables</option>
            <option value="Marketing & Advertising">Marketing & Advertising</option>
            <option value="Office Supplies">Office Supplies</option>
            <option value="Miscellaneous & Refreshments">Miscellaneous & Refreshments</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
        </div>

        <div className="relative">
          <select
            value={paymentMethod}
            onChange={(e) => {
              setPaymentMethod(e.target.value);
              setPage(0);
            }}
            className="h-9 pl-3 pr-8 bg-white border border-brand-border rounded-md text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green appearance-none"
          >
            <option value="">All Payment Methods</option>
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="mobile_wallet">Mobile Wallet</option>
            <option value="card">Card</option>
            <option value="other">Other</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
        </div>
      </div>

      {/* Error */}
      {isError && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && (
        <>
          {expenses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center bg-white border rounded-xl">
              <DollarSign className="w-12 h-12 text-brand-muted/30 mb-3" />
              <p className="font-bold text-brand-black">No expenses found</p>
              <p className="text-brand-muted mt-1">
                {search || category || paymentMethod
                  ? 'Try adjusting your search or filters.'
                  : 'Log shop operational expenses using the button above.'}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Date</th>
                      <th className="text-left px-4 py-3">Category</th>
                      <th className="text-left px-4 py-3">Description</th>
                      <th className="text-left px-4 py-3">Vendor</th>
                      <th className="text-right px-4 py-3">Amount</th>
                      <th className="text-left px-4 py-3">Method</th>
                      <th className="text-center px-4 py-3">Receipt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {expenses.map((expense: Expense) => (
                      <tr
                        key={expense.id}
                        onClick={() => {
                          setSelectedExpense(expense);
                          setFormModalOpen(true);
                        }}
                        className="hover:bg-brand-bg/60 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                          <span className="flex items-center space-x-1">
                            <CalendarDays className="w-3.5 h-3.5 text-brand-red shrink-0" />
                            <span>{formatDate(expense.expense_date)}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 font-semibold text-brand-black">
                          {expense.category}
                        </td>
                        <td className="px-4 py-3 font-medium text-brand-black">
                          {expense.description}
                        </td>
                        <td className="px-4 py-3 text-brand-muted">
                          {expense.vendor || '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-red-700">
                          {formatCurrency(expense.amount)}
                        </td>
                        <td className="px-4 py-3 text-brand-black uppercase font-semibold text-[10px]">
                          {expense.payment_method || 'cash'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {expense.receipt_path ? (
                            <span className="inline-flex items-center space-x-1 text-brand-green font-semibold">
                              <FileText className="w-3.5 h-3.5" />
                              <span>View</span>
                            </span>
                          ) : (
                            <span className="text-brand-muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {totalCount > PAGE_SIZE && <Pagination page={page} totalCount={totalCount} onPageChange={setPage} />}
        </>
      )}

      {/* Expense Modal */}
      <Modal
        isOpen={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        title={selectedExpense ? 'Edit Expense Record' : 'Log New Operating Expense'}
        maxWidth="md"
      >
        <ExpenseForm
          expense={selectedExpense ?? undefined}
          onSuccess={() => {
            setFormModalOpen(false);
            void refetch();
          }}
          onCancel={() => setFormModalOpen(false)}
        />
      </Modal>
    </div>
  );
};
